import {
  Alert,
  Badge,
  Box,
  Container,
  Header,
  Select,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import ScanField from 'features/scan/components/ScanField';
import { ScanTypes } from 'features/scan/utils/parseScanCode';
import PropTypes from 'prop-types';
import React from 'react';

import { ScanStatus, STATUS_META } from './classifyScan';

const FILTER_OPTIONS = [
  { label: 'All statuses', value: 'all' },
  { label: 'Counted', value: ScanStatus.COUNTED },
  { label: 'Not expected', value: ScanStatus.NOT_EXPECTED },
  { label: 'Other SKU', value: ScanStatus.OTHER_SKU },
];

const feedBoxStyle = {
  border: '1px solid var(--color-border-divider-default, rgb(235,235,240))',
  borderRadius: 10,
  maxHeight: 250,
  overflow: 'auto',
};

const rowStyle = (even) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  padding: '7px 12px',
  borderBottom: '1px solid rgb(240,240,244)',
  background: even ? 'rgb(252,252,253)' : 'transparent',
  fontFamily: 'Monaco, monospace',
  fontSize: 12,
});

export default function ScanFeed({
  counted,
  sessionCount,
  visibleScans,
  totalScans,
  filterValue,
  onFilterChange,
  onScan,
  inlineError = '',
}) {
  const selectedFilter = FILTER_OPTIONS.find((o) => o.value === filterValue) || FILTER_OPTIONS[0];

  return (
    <Container
      header={(
        <Header actions={<Badge color="blue">{`${counted} counted`}</Badge>}>
          Scan feed
        </Header>
      )}
    >
      <SpaceBetween size="s">
        <ScanField
          id="reconciliation-traveler-scanner"
          accept={[ScanTypes.TRAVELER]}
          onScan={({ id }) => onScan(id)}
          autoFocus
          popoverPosition="right"
          description="Handheld wedge scanner keeps focus for rapid back-to-back scans"
        />

        {inlineError ? <Alert type="warning">{inlineError}</Alert> : null}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <Box fontWeight="bold" fontSize="body-s" color="text-body-secondary">
            {`This session (${sessionCount})`}
          </Box>
          <div style={{ minWidth: 180 }}>
            <Select
              selectedOption={selectedFilter}
              options={FILTER_OPTIONS}
              onChange={({ detail }) => onFilterChange(detail.selectedOption.value)}
              expandToViewport
            />
          </div>
        </div>

        <div style={feedBoxStyle}>
          {visibleScans.length === 0 ? (
            <Box
              color="text-status-inactive"
              textAlign="center"
              padding="m"
              fontSize="body-s"
            >
              {totalScans === 0
                ? 'Scan a traveler to begin'
                : 'No scans match this filter'}
            </Box>
          ) : (
            visibleScans.map((scan, index) => (
              <div key={scan.key} style={rowStyle(index % 2 === 1)}>
                <span style={{ color: 'rgb(0,108,224)' }}>{scan.id}</span>
                <StatusIndicator type={STATUS_META[scan.status].indicator}>
                  {STATUS_META[scan.status].label}
                </StatusIndicator>
              </div>
            ))
          )}
        </div>
        {totalScans > 0 ? (
          <Box textAlign="center" fontSize="body-s" color="text-status-inactive">
            {`Newest first · scroll for all ${totalScans}`}
          </Box>
        ) : null}
      </SpaceBetween>
    </Container>
  );
}

ScanFeed.propTypes = {
  counted: PropTypes.number.isRequired,
  sessionCount: PropTypes.number.isRequired,
  visibleScans: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    id: PropTypes.string,
    status: PropTypes.string,
  })).isRequired,
  totalScans: PropTypes.number.isRequired,
  filterValue: PropTypes.string.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onScan: PropTypes.func.isRequired,
  inlineError: PropTypes.string,
};
